// External Imports
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import Modal from "../UI/Modal";
import Heading from "../UI/Heading";
import { MdClose } from "react-icons/md";
import VehicleSelector from "../vehicles-page/VehicleSelector";
import DetailPair from "../UI/DetailPair";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import { requestAdminAPI } from "../../api/posh-api";
import LinkPair from "../UI/LinkPair";
import SelectPair from "../UI/SelectPair";
import SignaturePad from "../UI/SignaturePad";
import ImageSelector from "../UI/ImageSelector";
import DetailList from "../UI/DetailList";
import { UtilityContext } from "../../context/util-context";
import { uploadBase64String } from "../../util/upload-base64";
import { useLocation } from "react-router-dom";
import { isoDateToString } from "../../util/format-date";
import { uploadImageToFirebase } from "../../util/upload-image-to-firebase";
import Switch from "../UI/Switch";
import { MdConstruction } from "react-icons/md";

/** ========================================================================
 * * AddClaim Component
 * This component renders a form to add a service.
 * ========================================================================== */

export default function AddClaim() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const bookingId = params.get("bookingId");

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [show, setShow] = useState({
    driver: true,
    incident: true,
    other: false,
    witness: false,
    police: false,
    injury: false,
    damage: false,
    maintenance: false,
  });
  const [vehicle, setVehicle] = useState();
  const mileageRef = useRef();

  /* ======================= FETCH BOOKING ======================= */
  const fetchBooking = async (bookingId) => {
    if (!bookingId) return;
    try {
      setLoading(true);
      setShow({
        driver: true,
        incident: true,
        other: true,
        witness: true,
        police: true,
        injury: true,
        damage: true,
      });
      const response = await requestAdminAPI("fetch", {
        table: "booking",
        id: bookingId,
        columns: [
          "customer { firstName lastName phone email address driversLicenseNumber }",
          "vehicle { id vin trackerId cornerImage year make { name } model license licenseState }",
        ],
      });
      document.getElementById(
        "driverName"
      ).value = `${response.customer.firstName} ${response.customer.lastName}`;
      document.getElementById("driverPhone").value = response.customer.phone;
      document.getElementById("driverEmail").value = response.customer.email;
      document.getElementById("driverAddress").value =
        response.customer.address;
      document.getElementById("driverLicense").value =
        response.customer.driversLicenseNumber;
      document.getElementById("bookingId").value = response.id;
      setVehicle(response.vehicle);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= FETCH VEHICLE STATS ======================= */
  const fetchVehicleStats = async () => {
    try {
      setLoading(true);
      // Send Request
      const response = await requestAdminAPI("fetch-vehicle-reports", {
        vehicles: [{ vin: vehicle.vin, trackerId: vehicle.trackerId }],
      });
      const report = response[0]?.report;
      if (report) mileageRef.current.value = report.mileage;
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== ADD SERVICE ====================== */
  const submitHandler = async (signature) => {
    try {
      setLoading(true);
      if (!vehicle) return;
      const payload = {
        vehicleId: vehicle.id,
        bookingId: document.getElementById("bookingId").value,
        driverName: document.getElementById("driverName").value,
        driverPhone: document.getElementById("driverPhone").value,
        driverEmail: document.getElementById("driverEmail").value,
        driverAddress: document.getElementById("driverAddress").value,
        driverLicense: document.getElementById("driverLicense").value,
        provider: document.getElementById("provider").value,
        policy: document.getElementById("policy").value,
        incidentDate: document.getElementById("incidentDate").value,
        incidentTime: document.getElementById("incidentTime").value,
        mileage: parseInt(document.getElementById("mileage").value),
        weather: document.getElementById("weather").value,
        location: document.getElementById("location").value,
        description: document.getElementById("description").value,
        otherName: document.getElementById("otherName").value,
        otherPhone: document.getElementById("otherPhone").value,
        otherEmail: document.getElementById("otherEmail").value,
        otherAddress: document.getElementById("otherAddress").value,
        otherLicense: document.getElementById("otherLicense").value,
        otherProvider: document.getElementById("otherProvider").value,
        otherPolicy: document.getElementById("otherPolicy").value,
        otherVehicle: document.getElementById("otherVehicle").value,
        otherVehicleLicense: document.getElementById("otherVehicleLicense")
          .value,
        otherVehicleColor: document.getElementById("otherVehicleColor").value,
        witnessName: document.getElementById("witnessName").value,
        witnessPhone: document.getElementById("witnessPhone").value,
        witnessStatement: document.getElementById("witnessStatement").value,
        reportNumber: document.getElementById("reportNumber").value,
        officerName: document.getElementById("officerName").value,
        injuries: document.getElementById("injuries").value === "Yes",
        injuryDetails: document.getElementById("injuryDetails").value,
        medical: document.getElementById("medical").value === "Yes",
        repairName: document.getElementById("repairName").value,
        cost: parseFloat(document.getElementById("cost").value),
        damageDetails: document.getElementById("damageDetails").value,
      };

      // Upload Signature
      const url = await uploadBase64String(
        `vehicles/${vehicle.id}/claims/${new Date().getTime()}.png`,
        signature
      );
      if (url) payload.signature = url;

      // Upload Police Report
      const policeReport = document.getElementById("report").files[0];
      if (policeReport)
        payload.report = await uploadImageToFirebase(
          `vehicles/${vehicle.id}/claims/report.png`,
          "Police Report",
          policeReport,
          setUploading
        );

      // Upload Receipts
      const receipts = document.getElementById("receipts").files;
      if (receipts.length > 0) {
        const receiptsArray = [];
        for (let i = 0; i < receipts.length; i++) {
          const receipt = receipts[i];
          const url = await uploadImageToFirebase(
            `vehicles/${vehicle.id}/claims/receipts/${receipt.name}`,
            "Receipts",
            receipt,
            setUploading
          );
          receiptsArray.push(url);
        }
        payload.receipts = receiptsArray;
      }

      // Upload Damage Photos
      const images = document.getElementById("images").files;
      if (images.length > 0) {
        const imagesArray = [];
        for (let i = 0; i < images.length; i++) {
          const image = images[i];
          const url = await uploadImageToFirebase(
            `vehicles/${vehicle.id}/claims/damage/${image.name}`,
            "Damage Photos",
            image,
            setUploading
          );
          imagesArray.push(url);
        }
        payload.images = imagesArray;
      }

      const response = await requestAdminAPI("add-claim", payload);

      if (show.maintenance) {
        // Create a payload for all maintenance information
        const maintenance = {
          vehicleId: vehicle.id,
          description: document.getElementById("damageDetails").value,
          priority: document.getElementById("priority").value,
          unavailable: document.getElementById("unavailable").value === "yes",
          startMileage: parseInt(document.getElementById("mileage").value),
        };

        if (payload.images && payload.images.length > 0)
          maintenance.image = payload.images[0];

        // Add Maintenance
        await requestAdminAPI("add-maintenance", maintenance);
      }
      navigate(`/claims/info?id=${response}`);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vehicle) fetchVehicleStats();
  }, [vehicle]);

  useEffect(() => {
    if (bookingId) fetchBooking(bookingId);
  }, [bookingId]);

  return (
    <div className="divider">
      <div className="container gap-8 lg:col-span-3">
        <Heading
          title="Add Claim"
          text="Add Booking ID to automatically fetch driver information. You can also start a claim from the booking information page."
        />
        <div className="info">
          <div className="lg:col-span-2">
            <VehicleSelector selected={vehicle?.id} onSelect={setVehicle} />
          </div>
          <div className="lg:col-span-2">
            <DetailPair
              edit={true}
              label="Booking ID"
              inputOptions={{
                required: true,
                type: "text",
                id: "bookingId",
                onChange: (e) => fetchBooking(e.target.value),
              }}
            />
          </div>
        </div>
        <Heading
          title="Driver Information"
          isBackArrow={false}
          onClick={() => setShow({ ...show, driver: !show.driver })}
        />
        <div className={`${show.driver ? "" : "hidden"} info`}>
          <div className="lg:col-span-2">
            <DetailPair
              edit={true}
              label="Full Name"
              inputOptions={{
                type: "text",
                id: "driverName",
              }}
            />
          </div>
          <DetailPair
            edit={true}
            label="Phone Number"
            inputOptions={{
              type: "text",
              id: "driverPhone",
            }}
          />
          <DetailPair
            edit={true}
            label="Email Address"
            inputOptions={{
              type: "text",
              id: "driverEmail",
            }}
          />
          <div className="lg:col-span-4">
            <DetailPair
              edit={true}
              label="Full Address"
              inputOptions={{
                type: "text",
                id: "driverAddress",
              }}
            />
          </div>
          <div className="lg:col-span-2">
            <DetailPair
              edit={true}
              label="Driver's License Number"
              inputOptions={{
                type: "text",
                id: "driverLicense",
              }}
            />
          </div>
          <DetailPair
            edit={true}
            label="Insurance Provider"
            inputOptions={{
              type: "text",
              id: "provider",
            }}
          />
          <DetailPair
            edit={true}
            label="Policy Number"
            inputOptions={{
              type: "text",
              id: "policy",
            }}
          />
        </div>

        <Heading
          title="Incident Details"
          isBackArrow={false}
          onClick={() => setShow({ ...show, incident: !show.incident })}
        />
        <div className={`${show.incident ? "" : "hidden"} info`}>
          <DetailPair
            edit={true}
            label="Incident Date"
            inputOptions={{
              type: "date",
              id: "incidentDate",
            }}
          />
          <DetailPair
            edit={true}
            label="Incident Time"
            inputOptions={{
              type: "time",
              id: "incidentTime",
            }}
          />
          <DetailPair
            edit={true}
            label="Vehicle Mileage"
            inputOptions={{
              type: "text",
              id: "mileage",
              ref: mileageRef,
            }}
          />
          <DetailPair
            edit={true}
            label="Weather Conditions"
            inputOptions={{
              type: "text",
              id: "weather",
            }}
          />
          <div className="lg:col-span-4">
            <DetailPair
              edit={true}
              label="Incident Location"
              inputOptions={{
                type: "text",
                id: "location",
              }}
            />
          </div>
          <div className="grid lg:col-span-4">
            <label className="text-sm text-stone-400">
              Incident Description:
            </label>
            <textarea
              type="text"
              id="description"
              placeholder="Describe what happened..."
              rows={4}
              className="form-input"
            />
          </div>
        </div>

        <Heading
          title="Other Party"
          isBackArrow={false}
          onClick={() => setShow({ ...show, other: !show.other })}
        />

        <div className={`${show.other ? "" : "hidden"} info`}>
          <div className="lg:col-span-2">
            <DetailPair
              edit={true}
              label="Full Name"
              inputOptions={{
                type: "text",
                id: "otherName",
              }}
            />
          </div>
          <DetailPair
            edit={true}
            label="Phone Number"
            inputOptions={{
              type: "text",
              id: "otherPhone",
            }}
          />
          <DetailPair
            edit={true}
            label="Email Address"
            inputOptions={{
              type: "text",
              id: "otherEmail",
            }}
          />
          <div className="lg:col-span-4">
            <DetailPair
              edit={true}
              label="Full Address"
              inputOptions={{
                type: "text",
                id: "otherAddress",
              }}
            />
          </div>
          <div className="lg:col-span-2">
            <DetailPair
              edit={true}
              label="Driver's License Number"
              inputOptions={{
                type: "text",
                id: "otherLicense",
              }}
            />
          </div>
          <DetailPair
            edit={true}
            label="Insurance Provider"
            inputOptions={{
              type: "text",
              id: "otherProvider",
            }}
          />
          <DetailPair
            edit={true}
            label="Policy Number"
            inputOptions={{
              type: "text",
              id: "otherPolicy",
            }}
          />
          <div className="lg:col-span-2">
            <DetailPair
              edit={true}
              label="Vehicle Make, Model, Year"
              inputOptions={{
                type: "text",
                id: "otherVehicle",
              }}
            />
          </div>
          <DetailPair
            edit={true}
            label="Vehicle Color"
            inputOptions={{
              type: "text",
              id: "otherVehicleColor",
            }}
          />
          <DetailPair
            edit={true}
            label="Vehicle License"
            inputOptions={{
              type: "text",
              id: "otherVehicleLicense",
            }}
          />
        </div>

        <Heading
          title="Witness Information"
          isBackArrow={false}
          onClick={() => setShow({ ...show, witness: !show.witness })}
        />
        <div className={`${show.witness ? "" : "hidden"} info`}>
          <div className="lg:col-span-2">
            <DetailPair
              edit={true}
              label="Full Name"
              inputOptions={{
                type: "text",
                id: "witnessName",
              }}
            />
          </div>
          <DetailPair
            edit={true}
            label="Phone Number"
            inputOptions={{
              type: "text",
              id: "witnessPhone",
            }}
          />
          <div className="grid lg:col-span-4">
            <label className="text-sm text-stone-400">Witness Statement:</label>
            <textarea
              type="text"
              id="witnessStatement"
              placeholder="What did the witness say..."
              rows={4}
              className="form-input"
            />
          </div>
        </div>

        <Heading
          title="Police Report"
          isBackArrow={false}
          onClick={() => setShow({ ...show, police: !show.police })}
        />

        <div className={`${show.police ? "" : "hidden"} info`}>
          <DetailPair
            edit={true}
            label="Report Number"
            inputOptions={{
              type: "text",
              id: "reportNumber",
            }}
          />
          <div className="lg:col-span-2">
            <DetailPair
              edit={true}
              label="Officer Full Name"
              inputOptions={{
                type: "text",
                id: "officerName",
              }}
            />
          </div>
          <LinkPair
            edit={true}
            label="Police Report"
            inputOptions={{
              type: "file",
              accept: "image/*, .pdf",
              id: "report",
            }}
          />
        </div>

        <Heading
          title="Injury and Medical"
          isBackArrow={false}
          onClick={() => setShow({ ...show, injury: !show.injury })}
        />
        <div className={`${show.injury ? "" : "hidden"} info`}>
          <SelectPair
            edit={true}
            label="Injuries"
            options={["Yes", "No"]}
            inputOptions={{
              id: "injuries",
            }}
          />
          <SelectPair
            edit={true}
            label="Medical Treatment"
            options={["Yes", "No"]}
            inputOptions={{
              id: "medical",
            }}
          />
          <LinkPair
            edit={true}
            label="Receipts"
            inputOptions={{
              type: "file",
              id: "receipts",
              accept: "image/*, .pdf",
              multiple: true,
            }}
          />
          <div className="grid lg:col-span-4">
            <label className="text-sm text-stone-400">Injury Details:</label>
            <textarea
              type="text"
              id="injuryDetails"
              placeholder="Describe the injuries..."
              rows={2}
              className="form-input"
            />
          </div>
        </div>

        <Heading
          title="Vehicle Damage"
          isBackArrow={false}
          onClick={() => setShow({ ...show, damage: !show.damage })}
        />
        <div className={`${show.damage ? "" : "hidden"} info`}>
          <div className="lg:col-span-2">
            <DetailPair
              edit={true}
              label="Report Shop Name"
              inputOptions={{
                type: "text",
                id: "repairName",
              }}
            />
          </div>
          <DetailPair
            edit={true}
            label="Damage Amount"
            inputOptions={{
              type: "number",
              step: "0.01",
              min: "0",
              id: "cost",
            }}
          />
          <LinkPair
            edit={true}
            label="Damage Photos"
            inputOptions={{
              type: "file",
              accept: "image/*",
              multiple: true,
              id: "images",
            }}
          />
          <div className="grid lg:col-span-4">
            <label className="text-sm text-stone-400">Damage Details:</label>
            <textarea
              type="text"
              id="damageDetails"
              placeholder="Describe the damage to vehicle..."
              rows={2}
              className="form-input"
            />
          </div>
        </div>
        <div className="grid content-center gap-4 lg:grid-cols-3">
          <div className="flex items-center space-x-2">
            <p className="text-base text-stone-200">Add Maintenance</p>
            <Switch
              title="Add Maintenance"
              on={show.maintenance}
              onSwitch={() =>
                setShow({ ...show, maintenance: !show.maintenance })
              }
              Icon={MdConstruction}
            />
          </div>

          {show.maintenance && (
            <Fragment>
              <SelectPair
                label="PRIORITY"
                options={[
                  { id: 0, name: "Low" },
                  { id: 1, name: "Medium" },
                  { id: 2, name: "High" },
                ]}
                edit={true}
                inputOptions={{
                  id: "priority",
                }}
              />
              <SelectPair
                label="VEHICLE AVAILABLITY"
                options={[
                  { id: "no", name: "Do not change" },
                  { id: "yes", name: "Mark as unavailable" },
                ]}
                edit={true}
                inputOptions={{
                  id: "unavailable",
                }}
              />
            </Fragment>
          )}
        </div>
      </div>
      <div className="sidebar">
        {vehicle && (
          <div
            onClick={(e) => navigate(`/vehicle/info?id=${vehicle?.id}`, e)}
            className="grid gap-4 clickable"
          >
            <ImageSelector
              image={vehicle?.cornerImage}
              classes="w-full min-h-[100px]"
              loading={loading}
            />
            <DetailList
              label="Vehicle Details"
              values={[
                `${vehicle?.year} ${vehicle?.make?.name} ${vehicle?.model}`,
                `${vehicle?.id}`,
                `${vehicle?.license} ${vehicle?.licenseState}`,
              ]}
              loading={loading}
            />
          </div>
        )}
        {loading ? (
          <div className="grid gap-2">
            <LoadingSpinnerWrapper />
            {uploading && (
              <p className="text-sm text-center text-stone-400">
                Uploading {uploading[0]}... {uploading[1]}%
              </p>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            <p className="text-sm text-center text-stone-400">
              Sign in the box below to start claim process. Date signed will be
              added automatically as today. You can make any changes later.
            </p>
            <SignaturePad setSignature={submitHandler} classes="grid gap-2" />
          </div>
        )}
      </div>
    </div>
  );
}
